/**
 * Whiskey&Co. Theme JavaScript
 * Version: 1.0.0
 */

'use strict';

// =============================================================================
// Theme Namespace & Config
// =============================================================================

const WhiskeyTheme = window.WhiskeyTheme || {};

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Format a price in yen (smallest unit = yen, no cents in Japan)
 * @param {number} cents - price in cents (Shopify stores price * 100)
 * @returns {string} formatted price string e.g. "¥3,000"
 */
function formatMoney(cents) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  const yen = Math.floor(parseInt(cents, 10) / 100);
  return '¥' + yen.toLocaleString('ja-JP');
}

/**
 * Debounce a function call
 * @param {Function} fn
 * @param {number} delay - milliseconds
 * @returns {Function}
 */
function debounce(fn, delay) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * Throttle a function call
 * @param {Function} fn
 * @param {number} limit - milliseconds
 * @returns {Function}
 */
function throttle(fn, limit) {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      fn.apply(this, args);
      inThrottle = true;
      setTimeout(() => { inThrottle = false; }, limit);
    }
  };
}

/**
 * Trap focus within an element (for modals/drawers)
 * @param {HTMLElement} element
 * @param {KeyboardEvent} event
 */
function trapFocus(element, event) {
  const focusable = element.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  const firstFocusable = focusable[0];
  const lastFocusable = focusable[focusable.length - 1];

  if (event.key === 'Tab') {
    if (event.shiftKey) {
      if (document.activeElement === firstFocusable) {
        lastFocusable.focus();
        event.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        firstFocusable.focus();
        event.preventDefault();
      }
    }
  }
}

/**
 * Show a toast notification
 * @param {string} message
 * @param {'success'|'error'|'info'} type
 * @param {number} duration - ms before auto-dismiss
 */
function showToast(message, type = 'success', duration = 3000) {
  let container = document.getElementById('toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'toast-container';
    container.className = 'toast-container';
    container.setAttribute('aria-live', 'polite');
    container.setAttribute('aria-atomic', 'false');
    document.body.appendChild(container);
  }

  const toast = document.createElement('div');
  toast.className = `toast toast--${type}`;
  toast.textContent = message;
  toast.setAttribute('role', 'status');

  container.appendChild(toast);

  // Auto-remove
  setTimeout(() => {
    toast.classList.add('is-leaving');
    toast.addEventListener('animationend', () => toast.remove(), { once: true });
  }, duration);
}

// =============================================================================
// Age Verification
// =============================================================================

const AgeVerification = {
  STORAGE_KEY: 'whiskey_age_verified',
  COOKIE_NAME: 'whiskey_age_verified',
  UNDERAGE_KEY: 'whiskey_age_denied',
  // hero.liquid等、モーダルより先（inline scriptなのでdeferのtheme.jsより早い
  // タイミング）に実行される動画/演出の開始スクリプトが、このイベントを
  // 待ってから再生を始める。リスナーはこのイベント発火より前に登録されて
  // いれば実行順は問題ない（addEventListenerは対象コードの読み込み順に
  // 依存しない）。
  CLEARED_EVENT: 'age-verification:cleared',

  init() {
    // underage-messageは設定のON/OFFに関わらず常にDOMに存在するため、
    // リセットボタンは分岐より前に一度だけバインドしておく。
    this.bindUnderageReset();

    const settings = WhiskeyTheme.settings || {};
    if (!settings.ageVerificationEnabled) {
      this.dispatchCleared();
      return;
    }

    // Check if already verified (localStorage)
    if (this.isVerified()) {
      this.hide();
      this.dispatchCleared();
      return;
    }

    // Check if previously denied
    if (this.isDenied()) {
      this.showUnderageMessage();
      return;
    }

    // Show the modal
    this.show();
    this.bindEvents();
  },

  dispatchCleared() {
    document.dispatchEvent(new CustomEvent(this.CLEARED_EVENT));
  },

  isVerified() {
    try {
      return localStorage.getItem(this.STORAGE_KEY) === 'true' || this.getCookie(this.COOKIE_NAME) === 'true';
    } catch (e) {
      return this.getCookie(this.COOKIE_NAME) === 'true';
    }
  },

  isDenied() {
    try {
      return sessionStorage.getItem(this.UNDERAGE_KEY) === 'true';
    } catch (e) {
      return false;
    }
  },

  show() {
    const modal = document.getElementById('age-verification-modal');
    if (!modal) return;

    modal.classList.remove('is-hidden');
    document.body.classList.add('modal-open');
    document.body.setAttribute('aria-hidden', 'false');

    // Set focus to first button
    setTimeout(() => {
      const firstBtn = modal.querySelector('button, [href], input');
      if (firstBtn) firstBtn.focus();
    }, 100);

    // Trap focus
    this._focusTrapHandler = (e) => {
      if (e.key === 'Escape') {
        // Don't allow closing via Escape
        e.preventDefault();
      }
      trapFocus(modal, e);
    };
    document.addEventListener('keydown', this._focusTrapHandler);
  },

  hide(onComplete) {
    const modal = document.getElementById('age-verification-modal');
    if (!modal) return;

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      modal.classList.remove('is-closing');
      modal.classList.add('is-hidden');
      document.body.classList.remove('modal-open');
      if (this._focusTrapHandler) {
        document.removeEventListener('keydown', this._focusTrapHandler);
      }
      if (onComplete) onComplete();
    };

    // 既に is-hidden（＝そもそも表示されていなかった、init()の確認済みユーザー
    // パス等）の場合はアニメーション不要。実際に見えていた場合のみ演出する。
    const wasVisible = !modal.classList.contains('is-hidden');
    const content = modal.querySelector('.age-verification-modal__content');
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (wasVisible && content && !reduceMotion) {
      // 「煙のように消える」演出（CSSの age-verification-smoke-disperse）が
      // 終わるまで is-hidden を付けない。animationend が発火しない端末向けに
      // タイムアウトもフォールバックとして仕込む（CSSのanimation時間0.6sと合わせる）。
      modal.classList.add('is-closing');
      content.addEventListener('animationend', finish, { once: true });
      setTimeout(finish, 700);
    } else {
      finish();
    }
  },

  verify() {
    // Save verification state
    try {
      localStorage.setItem(this.STORAGE_KEY, 'true');
    } catch (e) {
      // localStorage not available (private mode etc.)
    }
    // Also set a session cookie as fallback
    this.setCookie(this.COOKIE_NAME, 'true', 365);
    this.hide(() => this.dispatchCleared());
  },

  deny() {
    try {
      sessionStorage.setItem(this.UNDERAGE_KEY, 'true');
    } catch (e) {}
    this.hide(() => this.showUnderageMessage());
  },

  showUnderageMessage() {
    const underageEl = document.getElementById('underage-message');
    if (underageEl) {
      underageEl.style.display = 'flex';
      document.body.classList.add('modal-open');
    }
  },

  bindUnderageReset() {
    const resetBtn = document.getElementById('underage-reset');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => this.resetVerification());
    }
  },

  // 誤クリック救済用。「20歳未満」判定をクリアして年齢確認モーダルを
  // 再表示する（isDenied()がtrueのままだと次回訪問時もunderage-message
  // が即表示され続けるため、sessionStorageのフラグを消すことが必須）。
  resetVerification() {
    try {
      sessionStorage.removeItem(this.UNDERAGE_KEY);
    } catch (e) {}

    const underageEl = document.getElementById('underage-message');
    if (underageEl) {
      underageEl.style.display = 'none';
    }
    document.body.classList.remove('modal-open');

    this.show();
    this.bindEvents();
  },

  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  },

  getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  },

  bindEvents() {
    // resetVerification()経由で複数回呼ばれ得る（isDenied()で最初から
    // underage-message表示だった場合はこれが未実行のままリセットされる
    // ため呼び直しが必要。一方、通常フロー→deny→resetの場合は既に
    // バインド済みのため、二重登録を防ぐガードが必要）。
    if (this._eventsBound) return;
    this._eventsBound = true;

    const yesBtn = document.getElementById('age-verify-yes');
    const noBtn = document.getElementById('age-verify-no');

    if (yesBtn) {
      yesBtn.addEventListener('click', () => this.verify());
    }

    if (noBtn) {
      noBtn.addEventListener('click', () => this.deny());
    }
  }
};

// =============================================================================
// Summer Holiday Notice — auto-popup on product pages / contact page
// (snippets/summer-holiday-notice.liquid, settings.summer_holiday_notice_enabled)
// =============================================================================

const SummerHolidayNotice = {
  STORAGE_KEY: 'whiskey_summer_notice_dismissed',

  init() {
    const modal = document.getElementById('summer-holiday-notice-modal');
    if (!modal) return; // setting disabled, or not rendered on this page

    if (this.isDismissed()) return;

    const show = () => {
      // 年齢確認モーダルの確認待ちの間に閉じられている可能性もあるため、
      // イベント発火時点で再度確認する。
      if (this.isDismissed()) return;
      modal.classList.remove('is-hidden');
      document.body.classList.add('modal-open');
    };

    // 年齢確認モーダルが表示中なら、それが解消されるまで待ってから出す
    // （2つのモーダルが重なって出ないように、AgeVerificationと同じ
    // age-verification:clearedイベントに乗る）。
    if (document.body.classList.contains('modal-open')) {
      document.addEventListener(AgeVerification.CLEARED_EVENT, show, { once: true });
    } else {
      show();
    }

    const close = () => {
      modal.classList.add('is-hidden');
      document.body.classList.remove('modal-open');
      try {
        sessionStorage.setItem(this.STORAGE_KEY, 'true');
      } catch (e) {
        // sessionStorage not available (private mode etc.) — will simply
        // reappear next page load, which is an acceptable fallback.
      }
    };

    modal.querySelectorAll('[data-summer-notice-close]').forEach((el) => {
      el.addEventListener('click', close);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.classList.contains('is-hidden')) close();
    });
  },

  isDismissed() {
    try {
      return sessionStorage.getItem(this.STORAGE_KEY) === 'true';
    } catch (e) {
      return false;
    }
  }
};

// =============================================================================
// Header Behavior (sticky + transparent-to-solid)
// =============================================================================

const Header = {
  el: null,
  scrollThreshold: 80,
  // hero__logo が完全に表示されてから2秒後に表示する
  // （heroKvLogoFade: 6s中92%地点で表示完了 = 5520ms + 2000ms。theme.css参照）
  heroRevealDelayMs: 7520,

  init() {
    this.el = document.getElementById('site-header');
    if (!this.el) return;

    // TOPページの透明ヘッダーはヒーローと被らないよう、読み込み直後は
    // 隠しておき、スクロール開始 or 一定時間経過のどちらか早い方で表示する
    if (this.el.classList.contains('site-header--transparent')) {
      this.initHeroReveal();
    }

    // Only apply scroll behavior if sticky
    if (!this.el.classList.contains('site-header--sticky')) return;

    this.update();
    window.addEventListener('scroll', throttle(() => this.update(), 16), { passive: true });
  },

  initHeroReveal() {
    this.el.classList.add('is-hidden');

    let revealed = false;
    const reveal = () => {
      if (revealed) return;
      revealed = true;
      this.el.classList.remove('is-hidden');
      window.removeEventListener('scroll', reveal);
    };

    window.addEventListener('scroll', reveal, { passive: true, once: true });
    setTimeout(reveal, this.heroRevealDelayMs);
  },

  update() {
    if (!this.el) return;
    const isScrolled = window.scrollY > this.scrollThreshold;
    this.el.classList.toggle('site-header--scrolled', isScrolled);
  }
};

// =============================================================================
// Mobile Menu
// =============================================================================

const MobileMenu = {
  toggle: null,
  nav: null,
  overlay: null,
  closeBtn: null,
  isOpen: false,

  init() {
    this.toggle = document.getElementById('mobile-menu-toggle');
    this.nav = document.getElementById('mobile-nav');
    this.overlay = document.getElementById('mobile-nav-overlay');
    this.closeBtn = document.getElementById('mobile-menu-close');

    if (!this.toggle || !this.nav) return;

    this.toggle.addEventListener('click', () => this.open());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
      if (this.isOpen) trapFocus(this.nav, e);
    });
  },

  open() {
    this.isOpen = true;
    this.nav.classList.add('is-open');
    this.nav.setAttribute('aria-hidden', 'false');
    if (this.overlay) {
      this.overlay.classList.add('is-visible');
      this.overlay.setAttribute('aria-hidden', 'false');
    }
    this.toggle.setAttribute('aria-expanded', 'true');
    document.body.classList.add('drawer-open');

    // Focus first item in nav
    setTimeout(() => {
      const firstLink = this.nav.querySelector('a, button');
      if (firstLink) firstLink.focus();
    }, 100);
  },

  close() {
    this.isOpen = false;
    this.nav.classList.remove('is-open');
    this.nav.setAttribute('aria-hidden', 'true');
    if (this.overlay) {
      this.overlay.classList.remove('is-visible');
      this.overlay.setAttribute('aria-hidden', 'true');
    }
    this.toggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('drawer-open');
    this.toggle.focus();
  }
};

// =============================================================================
// Cart Module
// =============================================================================

const Cart = {
  drawer: null,
  overlay: null,
  toggleBtn: null,
  closeBtn: null,
  body: null,
  footer: null,
  countEl: null,
  isOpen: false,
  state: null,
  pendingLines: null,

  init() {
    this.pendingLines = new Set();
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-drawer-overlay');
    this.toggleBtn = document.getElementById('cart-toggle');
    this.closeBtn = document.getElementById('cart-drawer-close');
    this.body = document.getElementById('cart-drawer-body');
    this.footer = document.getElementById('cart-drawer-footer');
    this.countEl = document.getElementById('cart-count');

    const settings = WhiskeyTheme.settings || {};
    if (settings.cartType === 'page') {
      // Let links go to cart page
      return;
    }

    if (!this.drawer || !this.toggleBtn) return;

    this.toggleBtn.addEventListener('click', () => {
      if (this.isOpen) this.close();
      else this.openAndLoad();
    });

    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
      if (this.isOpen) trapFocus(this.drawer, e);
    });

    // Listen for custom add-to-cart events
    document.addEventListener('cart:updated', () => this.loadCart());
  },

  open() {
    this.isOpen = true;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    if (this.overlay) {
      this.overlay.classList.add('is-visible');
      this.overlay.setAttribute('aria-hidden', 'false');
    }
    this.toggleBtn.setAttribute('aria-expanded', 'true');
    document.body.classList.add('drawer-open');

    setTimeout(() => {
      const firstEl = this.drawer.querySelector('button, [href], input');
      if (firstEl) firstEl.focus();
    }, 100);
  },

  close() {
    this.isOpen = false;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    if (this.overlay) {
      this.overlay.classList.remove('is-visible');
      this.overlay.setAttribute('aria-hidden', 'true');
    }
    this.toggleBtn.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('drawer-open');
    this.toggleBtn.focus();
  },

  openAndLoad() {
    this.open();
    this.loadCart();
  },

  async loadCart() {
    if (!this.body) return;
    this.body.innerHTML = `<div class="cart-drawer__loading"><span class="loading-spinner"></span></div>`;

    try {
      const response = await fetch('/cart.js', {
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Cart fetch failed');
      const cart = await response.json();
      this.state = cart;
      this.render(cart);
      this.updateCount(cart.item_count);
    } catch (error) {
      console.error('[WhiskeyTheme] Cart load error:', error);
      this.body.innerHTML = `<p class="cart-drawer__error">${(WhiskeyTheme.strings || {}).cartError || 'エラーが発生しました'}</p>`;
    }
  },

  render(cart) {
    if (!this.body || !this.footer) return;

    // render()は数量±/削除ボタンをDOMごと作り直すため、直前の描画時点で
    // 「処理中」だった行番号の記録はここで必ずリセットする
    // （リセットしないと、同じ行番号が二度と押せなくなる）。
    this.pendingLines = new Set();

    if (cart.item_count === 0) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true">
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <path d="M16 10a4 4 0 0 1-8 0"/>
          </svg>
          <p class="cart-drawer__empty-title">${(WhiskeyTheme.strings || {}).cartEmpty || 'カートは空です'}</p>
          <p class="cart-drawer__empty-message">ショッピングを始めましょう</p>
          <a href="/collections/all" class="btn btn--outline">商品を見る</a>
        </div>
      `;
      this.footer.style.display = 'none';
      return;
    }

    const itemsHTML = cart.items.map(item => this.renderItem(item)).join('');
    this.body.innerHTML = `<div class="cart-drawer__items">${itemsHTML}</div>`;
    this.footer.style.display = 'flex';
    DiscountReminder.renderIntoDrawer(this.footer);

    // 小計は割引前（original_total_price）。cart.total_priceは既に割引後の金額なので、
    // 小計欄にそのまま出すと下の割引額と二重に引かれているように見えてしまう。
    const totalEl = document.getElementById('cart-drawer-total');
    if (totalEl) totalEl.textContent = formatMoney(cart.original_total_price);

    const discounts = cart.cart_level_discount_applications || [];
    const discountsEl = document.getElementById('cart-drawer-discounts');
    if (discountsEl) {
      discountsEl.innerHTML = discounts.map(d => `
        <div class="cart-drawer__discount-row">
          <span>${d.title}</span>
          <span>−${formatMoney(d.total_allocated_amount)}</span>
        </div>
      `).join('');
    }

    const grandTotalRow = document.getElementById('cart-drawer-grand-total-row');
    const grandTotalEl = document.getElementById('cart-drawer-grand-total');
    if (grandTotalRow && grandTotalEl) {
      grandTotalRow.style.display = discounts.length > 0 ? 'flex' : 'none';
      grandTotalEl.textContent = formatMoney(cart.total_price);
    }

    // Bind quantity change events
    this.body.querySelectorAll('[data-cart-qty-change]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const line = parseInt(btn.dataset.line, 10);
        // 同じ行の更新リクエストが処理中の間は、連打による多重送信・
        // 取りこぼしを防ぐため無視する（応答が返ればrender()で解除される）。
        if (this.pendingLines.has(line)) return;
        const delta = parseInt(btn.dataset.delta, 10);
        const currentItem = cart.items[line - 1];
        if (!currentItem) return;
        const newQty = currentItem.quantity + delta;
        this.pendingLines.add(line);
        this.setLineDisabled(line, true);
        await this.updateLineItem(line, Math.max(0, newQty));
      });
    });

    this.body.querySelectorAll('[data-cart-remove]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const line = parseInt(btn.dataset.line, 10);
        if (this.pendingLines.has(line)) return;
        this.pendingLines.add(line);
        this.setLineDisabled(line, true);
        await this.updateLineItem(line, 0);
      });
    });
  },

  setLineDisabled(line, disabled) {
    if (!this.body) return;
    this.body.querySelectorAll('[data-line="' + line + '"]').forEach(el => {
      el.disabled = disabled;
    });
  },

  renderItem(item) {
    const imageURL = item.image
      ? item.image.replace(/(\.\w+)$/, '_80x80$1')
      : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"%3E%3Crect fill="%232e2e2e" width="80" height="80"/%3E%3C/svg%3E';

    const line = item.line || (this.state ? this.state.items.indexOf(item) + 1 : 1);

    return `
      <div class="cart-item" data-variant-id="${item.variant_id}">
        <img
          src="${imageURL}"
          alt="${item.title}"
          class="cart-item__image"
          width="80"
          height="80"
          loading="lazy"
        >
        <div class="cart-item__details">
          <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' && item.variant_title !== 'ご自宅用' ? `<span class="cart-item__variant">${item.variant_title}</span>` : ''}
          <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
          <div class="cart-item__quantity">
            <button class="cart-item__qty-btn" data-cart-qty-change data-line="${line}" data-delta="-1" aria-label="数量を減らす">−</button>
            <span class="cart-item__qty-value">${item.quantity}</span>
            <button class="cart-item__qty-btn" data-cart-qty-change data-line="${line}" data-delta="1" aria-label="数量を増やす">＋</button>
          </div>
        </div>
        <button class="cart-item__remove" data-cart-remove data-line="${line}" aria-label="${item.product_title}を削除">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    `;
  },

  async updateLineItem(line, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if (!response.ok) throw new Error('Cart update failed');
      const cart = await response.json();
      this.state = cart;
      this.render(cart);
      this.updateCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (error) {
      console.error('[WhiskeyTheme] Cart update error:', error);
      showToast((WhiskeyTheme.strings || {}).cartError || 'エラーが発生しました', 'error');
      // 失敗時はrender()が呼ばれずボタンが再作成されないため、
      // ここで明示的にpending状態を解除してボタンを再度押せるようにする。
      if (this.pendingLines) {
        this.pendingLines.delete(line);
        this.setLineDisabled(line, false);
      }
    }
  },

  updateCount(count) {
    if (!this.countEl) return;
    this.countEl.textContent = count > 0 ? String(count) : '';

    // Animate badge
    if (count > 0) {
      this.countEl.style.transform = 'scale(1.3)';
      setTimeout(() => { this.countEl.style.transform = ''; }, 200);
    }
  }
};

// =============================================================================
// Add to Cart (AJAX)
// =============================================================================

const AddToCart = {
  init() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-add-to-cart-form]');
      if (!form) return;

      e.preventDefault();
      await this.handleSubmit(form);
    });

    // Quick add buttons
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      await this.addVariant(variantId, 1, btn);
    });

    // Add-to-cart buttons (product cards, product detail page) — not wrapped
    // in a <form>, so they rely on this click handler rather than form submit.
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-add-to-cart]');
      if (!btn || btn.disabled) return;
      e.preventDefault();

      const scope = btn.closest('.product-main, .product-card, .product-card-overlay') || document;

      let variantId = btn.dataset.variantId;
      if (!variantId) {
        const variantInput = scope.querySelector('[data-variant-id]');
        variantId = variantInput ? variantInput.value : null;
      }
      if (!variantId) return;

      const qtyInput = scope.querySelector('[data-quantity-input]');
      const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      await this.addVariant(variantId, quantity, btn);
    });
  },

  async handleSubmit(form) {
    const submitBtn = form.querySelector('[type="submit"]');
    const formData = new FormData(form);

    if (submitBtn) {
      submitBtn.classList.add('btn--loading');
      submitBtn.disabled = true;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/javascript' },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Add to cart failed');
      }

      const item = await response.json();

      // Show success feedback
      showToast((WhiskeyTheme.strings || {}).cartAdded || 'カートに追加しました', 'success');

      // Update cart count and optionally open drawer
      document.dispatchEvent(new CustomEvent('cart:updated'));

      const settings = WhiskeyTheme.settings || {};
      if (settings.cartType === 'drawer') {
        Cart.openAndLoad();
      }

    } catch (error) {
      console.error('[WhiskeyTheme] Add to cart error:', error);
      showToast(error.message || (WhiskeyTheme.strings || {}).cartError || 'エラーが発生しました', 'error');
    } finally {
      if (submitBtn) {
        submitBtn.classList.remove('btn--loading');
        submitBtn.disabled = false;
      }
    }
  },

  async addVariant(variantId, quantity = 1, btn = null) {
    if (btn) {
      btn.classList.add('btn--loading');
      btn.disabled = true;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Add to cart failed');
      }

      await response.json();
      showToast((WhiskeyTheme.strings || {}).cartAdded || 'カートに追加しました', 'success');
      document.dispatchEvent(new CustomEvent('cart:updated'));

      const settings = WhiskeyTheme.settings || {};
      if (settings.cartType === 'drawer') {
        Cart.openAndLoad();
      }

    } catch (error) {
      console.error('[WhiskeyTheme] Quick add error:', error);
      showToast(error.message || (WhiskeyTheme.strings || {}).cartError || 'エラーが発生しました', 'error');
    } finally {
      if (btn) {
        btn.classList.remove('btn--loading');
        btn.disabled = false;
      }
    }
  }
};

// =============================================================================
// Section Anchor Resolver
// =============================================================================

/**
 * Resolves an in-page anchor selector (e.g. "#section-hero") to its actual
 * DOM element. JSON-template sections render with `section.id` prefixed as
 * `template--<n>__<key>` (Online Store 2.0), so a literal `id="section-hero"`
 * rarely exists. Falls back to matching any element whose id ends with
 * `__<key>` before giving up.
 */
function resolveSectionAnchor(selector) {
  if (!selector || selector === '#') return null;
  const exact = document.querySelector(selector);
  if (exact) return exact;
  const key = selector.replace(/^#/, '').replace(/^section-/, '');
  if (!key) return null;
  return document.querySelector(`[id$="__${key}"]`);
}

// =============================================================================
// Smooth Scroll
// =============================================================================

const SmoothScroll = {
  init() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (href === '#') return;

      const target = resolveSectionAnchor(href);
      if (!target) return;

      e.preventDefault();
      const headerOffset = (document.getElementById('site-header')?.offsetHeight || 72) + 16;
      const targetTop = target.getBoundingClientRect().top + window.scrollY - headerOffset;

      window.scrollTo({
        top: targetTop,
        behavior: 'smooth'
      });
    });
  }
};

// =============================================================================
// Intersection Observer — Fade-in Animations
// =============================================================================

const AnimationObserver = {
  observer: null,

  init() {
    const settings = WhiskeyTheme.settings || {};
    if (!settings.enableAnimations) return;

    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const animationStyle = settings.animationStyle || 'fade-up';

    // Auto-tag common content blocks so individual sections don't each need
    // to add the class by hand — this is what makes content "rise" into
    // view as the page is scrolled.
    const autoRevealSelectors = [
      '.section__header',
      '.business-model__item',
      '.brand-theme__concept',
      '.brand-story__inner',
      '.image-banner__box',
      '.news-item',
      '.company-table__row',
      '.distillery-card',
      '.product-grid__item',
      '.distillery-feature__media',
      '.distillery-feature__content'
    ];
    document.querySelectorAll(autoRevealSelectors.join(',')).forEach((el) => {
      if (!el.classList.contains('animate-on-scroll')) {
        el.classList.add('animate-on-scroll');
      }
      // Stagger siblings within the same grid/list (see .animate-children
      // nth-child delays in theme.css) instead of having them all rise at once.
      if (el.parentElement) {
        el.parentElement.classList.add('animate-children');
      }
    });

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this.observer.unobserve(entry.target);
          }
        });
      },
      {
        // threshold was 0.1 (10% of the target's own area must be visible).
        // For very tall cards (e.g. the 9:16 token-intro cards, ~1350px)
        // that requires a large absolute scroll distance before firing, so
        // by the time it fires a large chunk of the card is already on
        // screen and the 600ms fade finishes before the user perceives it
        // rising into view. threshold:0 fires as soon as the element's edge
        // crosses into the (rootMargin-adjusted) viewport, independent of
        // the element's own height, so the fade plays out while it's
        // actually scrolling into view.
        threshold: 0,
        rootMargin: '0px 0px -40px 0px'
      }
    );

    // Add animation class to elements that should animate
    document.querySelectorAll('.animate-on-scroll').forEach(el => {
      // Apply the correct transform class based on animation style
      if (!el.classList.contains('fade-in') &&
          !el.classList.contains('slide-in-left') &&
          !el.classList.contains('slide-in-right')) {
        el.classList.add(animationStyle === 'fade-up' ? 'fade-up' : 'fade-in');
      }
      this.observer.observe(el);
    });
  },

  /**
   * Re-observe newly added elements (e.g., after dynamic content load)
   * @param {HTMLElement} container
   */
  observe(container) {
    if (!this.observer) return;
    container.querySelectorAll('.animate-on-scroll:not(.is-visible)').forEach(el => {
      this.observer.observe(el);
    });
  }
};

// =============================================================================
// FiNANCiE Token Verification
// =============================================================================

/**
 * Check if a wallet address holds a FiNANCiE token for this project.
 * Calls the configured API endpoint and returns a boolean.
 *
 * @param {string} walletAddress - The user's crypto wallet address
 * @returns {Promise<boolean>} true if the wallet holds the required token
 */
async function checkFinancieToken(walletAddress) {
  const settings = WhiskeyTheme.settings || {};
  const endpoint = settings.financieEndpoint;
  const projectId = settings.financieProjectId;

  if (!endpoint) {
    console.warn('[WhiskeyTheme] FiNANCiE API endpoint not configured.');
    return false;
  }

  if (!walletAddress || typeof walletAddress !== 'string') {
    console.warn('[WhiskeyTheme] Invalid wallet address provided.');
    return false;
  }

  // Basic wallet address validation (EVM-style 0x...)
  const normalizedAddress = walletAddress.trim().toLowerCase();
  if (!/^0x[0-9a-f]{40}$/i.test(normalizedAddress)) {
    console.warn('[WhiskeyTheme] Wallet address format appears invalid.');
    return false;
  }

  try {
    const url = new URL(endpoint);
    url.searchParams.set('wallet', normalizedAddress);
    if (projectId) url.searchParams.set('project_id', projectId);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      // Timeout via AbortController
      signal: AbortSignal.timeout ? AbortSignal.timeout(10000) : undefined
    });

    if (!response.ok) {
      throw new Error(`API responded with status ${response.status}`);
    }

    const data = await response.json();

    // Expected response shape: { holds_token: true, token_count: 2 }
    // Adjust based on actual FiNANCiE API response format
    return Boolean(data.holds_token || data.is_holder || data.eligible);

  } catch (error) {
    if (error.name === 'AbortError' || error.name === 'TimeoutError') {
      console.error('[WhiskeyTheme] FiNANCiE API request timed out.');
    } else {
      console.error('[WhiskeyTheme] FiNANCiE check error:', error);
    }
    return false;
  }
}

// =============================================================================
// FiNANCiE Member UI Handler
// =============================================================================

const FinancieUI = {
  init() {
    // Handle FiNANCiE verification forms
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-financie-verify-form]');
      if (!form) return;
      e.preventDefault();
      await this.handleVerification(form);
    });
  },

  async handleVerification(form) {
    const input = form.querySelector('[name="wallet_address"]');
    const submitBtn = form.querySelector('[type="submit"]');
    const resultEl = form.querySelector('[data-financie-result]');
    const strings = WhiskeyTheme.strings || {};

    if (!input) return;

    const walletAddress = input.value.trim();
    if (!walletAddress) return;

    if (submitBtn) {
      submitBtn.classList.add('btn--loading');
      submitBtn.disabled = true;
      submitBtn.textContent = strings.financieChecking || '確認中...';
    }

    try {
      const isHolder = await checkFinancieToken(walletAddress);

      if (resultEl) {
        if (isHolder) {
          resultEl.textContent = '✓ FiNANCiEトークンを保有しています。限定商品をご利用いただけます。';
          resultEl.className = 'financie-result financie-result--success';
        } else {
          resultEl.textContent = 'このウォレットにはFiNANCiEトークンが見つかりませんでした。';
          resultEl.className = 'financie-result financie-result--error';
        }
        resultEl.style.display = 'block';
      }

      if (isHolder) {
        // Dispatch event so other components can react
        document.dispatchEvent(new CustomEvent('financie:verified', {
          detail: { walletAddress, isHolder }
        }));
      }

    } catch (error) {
      if (resultEl) {
        resultEl.textContent = strings.financieError || '確認に失敗しました。再度お試しください。';
        resultEl.className = 'financie-result financie-result--error';
        resultEl.style.display = 'block';
      }
    } finally {
      if (submitBtn) {
        submitBtn.classList.remove('btn--loading');
        submitBtn.disabled = false;
        submitBtn.textContent = 'ウォレットを確認する';
      }
    }
  }
};

// =============================================================================
// Cart Count Refresh (for initial page load)
// =============================================================================

const CartCount = {
  async init() {
    // Fetch current cart count on page load to keep badge in sync
    try {
      const response = await fetch('/cart.js', {
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) return;
      const cart = await response.json();
      Cart.updateCount(cart.item_count);
    } catch (e) {
      // Silently fail — count will just show server-rendered value
    }
  }
};

// =============================================================================
// Accordion / FAQ
// =============================================================================

const Accordion = {
  init() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-accordion-trigger]');
      if (!trigger) return;

      const item = trigger.closest('[data-accordion-item]');
      if (!item) return;

      const isOpen = item.classList.contains('is-open');
      const accordion = item.closest('[data-accordion]');

      // If not allowing multiple open, close others
      if (accordion && !accordion.dataset.accordionMultiple) {
        accordion.querySelectorAll('[data-accordion-item].is-open').forEach(openItem => {
          if (openItem !== item) {
            openItem.classList.remove('is-open');
            const openTrigger = openItem.querySelector('[data-accordion-trigger]');
            if (openTrigger) openTrigger.setAttribute('aria-expanded', 'false');
            const openContent = openItem.querySelector('[data-accordion-content]');
            if (openContent) openContent.style.maxHeight = null;
          }
        });
      }

      item.classList.toggle('is-open', !isOpen);
      trigger.setAttribute('aria-expanded', String(!isOpen));

      const content = item.querySelector('[data-accordion-content]');
      if (content) {
        if (!isOpen) {
          content.style.maxHeight = content.scrollHeight + 'px';
        } else {
          content.style.maxHeight = null;
        }
      }
    });
  }
};

// =============================================================================
// Tabs
// =============================================================================

const Tabs = {
  init() {
    document.addEventListener('click', (e) => {
      const tab = e.target.closest('[data-tab-trigger]');
      if (!tab) return;

      const tabGroup = tab.closest('[data-tabs]');
      if (!tabGroup) return;

      const targetId = tab.dataset.tabTrigger;

      // Deactivate all tabs in this group
      tabGroup.querySelectorAll('[data-tab-trigger]').forEach(t => {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
      });
      tabGroup.querySelectorAll('[data-tab-panel]').forEach(p => {
        p.classList.remove('is-active');
        p.setAttribute('aria-hidden', 'true');
      });

      // Activate selected tab
      tab.classList.add('is-active');
      tab.setAttribute('aria-selected', 'true');
      const panel = tabGroup.querySelector(`[data-tab-panel="${targetId}"]`);
      if (panel) {
        panel.classList.add('is-active');
        panel.setAttribute('aria-hidden', 'false');
      }
    });
  }
};

// =============================================================================
// Serving Recommendation Carousel — 商品詳細「おすすめの飲み方」。
// レシピが複数ある場合、1枚見せのスライドを矢印/ドット/スワイプで切り替える。
// overflow-x:auto + scroll-snapのネイティブスクロールでスワイプに対応し、
// 矢印/ドットクリック時のみJSでscrollToを呼ぶ（ProductCarouselの
// 120msデバウンス補正と同じ考え方でscrollイベントを間引く）。
// =============================================================================

const ServingRecommendationCarousel = {
  init() {
    document.querySelectorAll('[data-serving-recommendation]').forEach((root) => {
      const viewport = root.querySelector('[data-serving-viewport]');
      const slides = Array.from(root.querySelectorAll('.serving-recommendation__slide'));
      const dots = Array.from(root.querySelectorAll('[data-serving-dot]'));
      const prevBtn = root.querySelector('[data-serving-prev]');
      const nextBtn = root.querySelector('[data-serving-next]');
      if (!viewport || slides.length <= 1) return;

      let currentIndex = 0;

      function updateActive(index) {
        currentIndex = index;
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        if (prevBtn) prevBtn.disabled = index === 0;
        if (nextBtn) nextBtn.disabled = index === slides.length - 1;
      }

      function goTo(index) {
        const clamped = Math.max(0, Math.min(slides.length - 1, index));
        viewport.scrollTo({ left: clamped * viewport.clientWidth, behavior: 'smooth' });
      }

      if (prevBtn) prevBtn.addEventListener('click', () => goTo(currentIndex - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => goTo(currentIndex + 1));
      dots.forEach((dot, i) => dot.addEventListener('click', () => goTo(i)));

      let scrollTimeout;
      viewport.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          const index = Math.round(viewport.scrollLeft / viewport.clientWidth);
          updateActive(index);
        }, 120);
      });

      updateActive(0);
    });
  }
};

// =============================================================================
// Section Background Gradients — スクロール量に連動して背景位置を継続的に
// 動かす（一度きりのCSSトランジションだと動く瞬間を見逃しやすいため、
// 常にスクロールに追従させて確実に動いているとわかるようにする）
// =============================================================================

const SectionGradient = {
  init() {
    this.els = Array.from(document.querySelectorAll('.section--gradient-rtl, .section--gradient-ltr'));
    if (!this.els.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.ticking = false;
    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll);
    this.update();
  },

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(this.update);
  },

  update() {
    const viewportHeight = window.innerHeight;
    this.els.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const total = rect.height + viewportHeight;
      const traveled = viewportHeight - rect.top;
      let progress = traveled / total;
      progress = Math.max(0, Math.min(1, progress));

      const isRtl = el.classList.contains('section--gradient-rtl');
      const position = isRtl ? progress * 100 : (1 - progress) * 100;
      el.style.backgroundPosition = `${position}% 0`;
    });
    this.ticking = false;
  }
};

// =============================================================================
// Hero KV Video Cover — YouTube iframeは固定%拡大だと縦長スマホ画面で
// レターボックス（空白）が生じるため、コンテナのアスペクト比に応じて
// 動画側の実サイズをJSで計算し、常に空白なく覆うようにする
// =============================================================================

const HeroVideoCover = {
  NATIVE_RATIO: 16 / 9,
  BUFFER: 1.1,

  init() {
    this.wrappers = Array.from(document.querySelectorAll('.hero__slide-video'));
    if (!this.wrappers.length) return;

    this.resize();
    window.addEventListener('resize', debounce(this.resize.bind(this), 150));
  },

  resize() {
    this.wrappers.forEach((wrapper) => {
      const iframe = wrapper.querySelector('iframe');
      const containerWidth = wrapper.offsetWidth;
      const containerHeight = wrapper.offsetHeight;
      if (!iframe || !containerWidth || !containerHeight) return;

      // 動画ファイル自体に上下の黒帯（レターボックス）が焼き込まれている場合、
      // data-video-crop-percent（片側あたりの黒帯の割合）の分だけ余分にズームし、
      // 実際に映像が写っている範囲だけでコンテナを覆うようにする
      const cropPercent = parseFloat(wrapper.dataset.videoCropPercent) || 0;
      const contentHeightRatio = 1 - (cropPercent / 100) * 2;
      const effectiveRatio = this.NATIVE_RATIO / contentHeightRatio;

      const containerRatio = containerWidth / containerHeight;
      let contentWidth;
      let contentHeight;

      if (containerRatio > effectiveRatio) {
        contentWidth = containerWidth;
        contentHeight = contentWidth / effectiveRatio;
      } else {
        contentHeight = containerHeight;
        contentWidth = contentHeight * effectiveRatio;
      }

      const width = contentWidth;
      const height = contentHeight / contentHeightRatio;

      iframe.style.width = `${width * this.BUFFER}px`;
      iframe.style.height = `${height * this.BUFFER}px`;
    });
  }
};

// =============================================================================
// Product Carousel — 商品一覧の横スクロール。自動再生はせず、
// 矢印ボタン／ドラッグ／スワイプで操作する。
// PC幅では商品数が画面幅に足りない場合のみアイテムを複製し、
// 端に来たら見えないようにscrollLeftを補正して無限ループに見せる。
// =============================================================================

const ProductCarousel = {
  init() {
    this.carousels = Array.from(document.querySelectorAll('[data-product-carousel]'));
    if (!this.carousels.length) return;

    this.carousels.forEach((carousel) => this.setupCarousel(carousel));
  },

  // PCは4個以上、スマホ/タブレットは2個以上の商品がある場合のみ
  // 横スクロール（クローン無限ループ）を有効にする。これ未満の場合は、
  // 同じ商品が連続で複製されてズラっと並ぶのを避け、静的な表示にする。
  DESKTOP_QUERY: '(min-width: 769px)',
  LOOP_THRESHOLD_DESKTOP: 4,
  LOOP_THRESHOLD_MOBILE: 2,

  setupCarousel(carousel) {
    const track = carousel.querySelector('[data-product-carousel-track]');
    const prevBtn = carousel.querySelector('[data-product-carousel-prev]');
    const nextBtn = carousel.querySelector('[data-product-carousel-next]');
    if (!track) return;

    // フィルタタブで絞り込んだ後も同じ仕組みでループを組み直せるよう、
    // 未加工のアイテム一覧をパース済みDOMとして保持しておく
    const sourceWrapper = document.createElement('div');
    sourceWrapper.innerHTML = track.innerHTML;
    let currentCategory = 'all';
    let setWidth = 0;
    let isLooping = false;
    let isAnimating = false;
    let animationEndTimer;
    let isTouching = false;
    let lastViewportWidth = window.innerWidth;

    const getFilteredItems = (category) => {
      const items = Array.from(sourceWrapper.querySelectorAll('.product-carousel__item'));
      // data-categoryは蒸留所とスピリッツ種別（ジン/ウイスキー/焼酎）を
      // 半角スペース区切りで複数保持できるため、完全一致ではなく含有チェックで絞り込む
      return category === 'all'
        ? items
        : items.filter((el) => (el.getAttribute('data-category') || '').split(' ').indexOf(category) !== -1);
    };

    // 商品数が十分にある場合だけ、アイテムを複製してクローンのバッファを
    // 持たせ、ドラッグ／スワイプ／ボタンのどの操作でも端で止まらず、
    // 見えないうちに巻き戻してシームレスに無限ループさせる。
    // 商品数が少ない場合はクローンせず、中央寄せの静的な表示にする。
    const buildLoop = () => {
      const items = getFilteredItems(currentCategory);
      const itemCount = items.length;
      const baseHtml = items.map((el) => el.outerHTML).join('');
      track.innerHTML = baseHtml;
      setWidth = track.scrollWidth;

      if (itemCount === 0) {
        isLooping = false;
        carousel.classList.add('product-carousel--static');
        return;
      }

      const isDesktop = window.matchMedia(this.DESKTOP_QUERY).matches;
      const threshold = isDesktop ? this.LOOP_THRESHOLD_DESKTOP : this.LOOP_THRESHOLD_MOBILE;

      if (itemCount < threshold) {
        isLooping = false;
        carousel.classList.add('product-carousel--static');
        track.scrollLeft = 0;
        return;
      }

      carousel.classList.remove('product-carousel--static');
      const containerWidth = carousel.offsetWidth;
      // 中央寄せ用paddingが大きいモバイルでは、1セットの幅だけで既に
      // containerWidthの3倍を超えることがあるため、まず複製数ではなく
      // 「最低2セット」を先に確保してから幅基準で追加複製する
      // （こうしないと2セット目の先頭アイテムが存在せず測定できない）
      let html = baseHtml + baseHtml;
      track.innerHTML = html;

      // トラックの中央寄せ用padding等の影響を受けないよう、複製したセット同士の
      // 先頭アイテム間の実際の距離（period）をDOM座標から直接測る
      while (track.scrollWidth < containerWidth * 3) {
        html += baseHtml;
        track.innerHTML = html;
      }

      const allItems = track.querySelectorAll('.product-carousel__item');
      const period = allItems[itemCount].offsetLeft - allItems[0].offsetLeft;

      // maintainLoopPositionは端に寄ったらperiod分だけ瞬間移動して補正するが、
      // その補正後の位置がまだ「反対側の端」判定圏内に入ってしまうと、
      // 補正→補正→補正…と永久に左右へ跳ね返り続け、カードが小刻みに
      // ブルブル震えて見える不具合になる。補正後も必ず安全圏（両端から
      // period/2以上離れた場所）に収まるには、可動範囲（maxScroll =
      // scrollWidth - containerWidth）がperiodの最低2倍は必要。
      // 従来は「+ period」（1倍）分しか余裕を確保しておらず、複製セット数
      // 次第でこの条件を満たせないことがあった。
      while (track.scrollWidth < containerWidth * 2 + period * 2) {
        html += baseHtml;
        track.innerHTML = html;
      }

      setWidth = period;
      track.scrollLeft = period;
      isLooping = true;
    };

    const updateNavState = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      const disabled = setWidth <= 0 || (!isLooping && maxScroll <= 1);
      if (prevBtn) prevBtn.toggleAttribute('disabled', disabled);
      if (nextBtn) nextBtn.toggleAttribute('disabled', disabled);
    };

    const maintainLoopPosition = () => {
      if (!isLooping) return;
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (track.scrollLeft < setWidth * 0.5 || track.scrollLeft > maxScroll - setWidth * 0.5) {
        const prevBehavior = track.style.scrollBehavior;
        track.style.scrollBehavior = 'auto';
        if (track.scrollLeft < setWidth * 0.5) {
          track.scrollLeft += setWidth;
        } else {
          track.scrollLeft -= setWidth;
        }
        track.style.scrollBehavior = prevBehavior;
      }
    };

    // ボタンによるsmoothスクロール中は補正(瞬間移動)を入れない。
    // アニメーション中にscrollLeftを書き換えるとブラウザの補間と競合して
    // 視覚的にガタつくため、アニメーション完了後に一度だけ補正する。
    const scrollByAmount = (direction) => {
      // カード幅+gapを「item幅 + 固定24px」で仮定していたが、実際のgapは
      // ブレークポイントごとに異なる（PC: --spacing-xl=32px、モバイル:
      // --spacing-xs=4px）ため、クリックのたびに数px単位でズレが蓄積し、
      // 見た目上「少しずつ中心からずれていく」不具合になっていた。
      // buildLoopのperiod計算と同じく、隣接アイテム同士の実際のoffsetLeftの
      // 差分をそのまま使うことで、gapの値に依存せず正確な1コマ分の距離を得る。
      const items = track.querySelectorAll('.product-carousel__item');
      let step;
      if (items.length >= 2) {
        step = items[1].offsetLeft - items[0].offsetLeft;
      } else if (items.length === 1) {
        step = items[0].getBoundingClientRect().width;
      } else {
        step = track.clientWidth * 0.8;
      }

      isAnimating = true;
      clearTimeout(animationEndTimer);
      track.scrollBy({ left: step * direction, behavior: 'smooth' });

      const finishAnimation = () => {
        isAnimating = false;
        maintainLoopPosition();
        updateNavState();
      };

      if ('onscrollend' in window) {
        track.addEventListener('scrollend', finishAnimation, { once: true });
      } else {
        animationEndTimer = setTimeout(finishAnimation, 500);
      }
    };

    if (prevBtn) prevBtn.addEventListener('click', () => scrollByAmount(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => scrollByAmount(1));

    // ドラッグ／スワイプ中に毎フレーム補正すると、巻き戻る瞬間が
    // 指を動かしている最中に見えてガクッとした切り替わりに感じられるため、
    // スクロールが落ち着いた後（一定時間イベントが来なくなった後）に
    // 一度だけ補正する。
    const debouncedMaintainLoopPosition = debounce(() => {
      if (!isAnimating && !isTouching) maintainLoopPosition();
    }, 120);
    const debouncedUpdateNavState = debounce(updateNavState, 50);
    track.addEventListener('scroll', () => {
      debouncedMaintainLoopPosition();
      debouncedUpdateNavState();
    }, { passive: true });

    // モバイルでタップ中（指がまだ画面に触れている間）にscrollLeftを
    // 書き換えると、指を離した瞬間のタップ判定（elementFromPoint）が
    // ジャンプ後の別カードを拾ってしまい、「タップしたのに違う商品に
    // 遷移する」不具合になる。指が触れている間は補正を止め、指を離した
    // 後（クリックイベントは通常ここでほぼ同時に発火し先に処理が終わる）に
    // あらためて補正をスケジュールする。
    track.addEventListener('touchstart', () => {
      isTouching = true;
    }, { passive: true });
    const endTouch = () => {
      isTouching = false;
      debouncedMaintainLoopPosition();
    };
    track.addEventListener('touchend', endTouch, { passive: true });
    track.addEventListener('touchcancel', endTouch, { passive: true });

    // モバイルはスクロール時のアドレスバー収縮/展開で「幅は変わらず高さだけ
    // 変わる」resizeイベントが頻発する。区別せずbuildLoop()（DOMを丸ごと
    // 作り直しscrollLeftもリセットする重い処理）を毎回走らせていたため、
    // ページをスクロールしてカルーセルを表示させただけでタップ直後に
    // カルーセルが再構築され、「タップしたのに違う商品に遷移する」
    // 不具合の一因になっていた。幅が実際に変化した場合のみ再構築する。
    window.addEventListener('resize', debounce(() => {
      if (window.innerWidth === lastViewportWidth) return;
      lastViewportWidth = window.innerWidth;
      buildLoop();
      updateNavState();
    }, 200));

    // 蒸留所／カテゴリ絞り込みタブ（同じセクション内のもの）
    const tabsSection = carousel.closest('.featured-products');
    const tabs = tabsSection ? Array.from(tabsSection.querySelectorAll('.tab-btn')) : [];
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        tabs.forEach((t) => {
          t.classList.remove('tab-btn--active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('tab-btn--active');
        tab.setAttribute('aria-selected', 'true');

        currentCategory = tab.getAttribute('data-tab');
        buildLoop();
        updateNavState();
      });
    });

    buildLoop();
    updateNavState();

    // マウスドラッグでの操作（タッチ端末はネイティブのスワイプに任せる）
    let isDragging = false;
    let dragStartX = 0;
    let scrollStartLeft = 0;

    track.addEventListener('mousedown', (e) => {
      isDragging = true;
      dragStartX = e.pageX;
      scrollStartLeft = track.scrollLeft;
      track.classList.add('is-dragging');
    });

    window.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
      track.scrollLeft = scrollStartLeft - (e.pageX - dragStartX);
    });

    const endDrag = () => {
      if (!isDragging) return;
      isDragging = false;
      track.classList.remove('is-dragging');
    };
    window.addEventListener('mouseup', endDrag);
    track.addEventListener('mouseleave', endDrag);
  }
};

// =============================================================================
// Light Parallax — photos drift slightly slower than the page scroll
// =============================================================================

const Parallax = {
  items: [],
  ticking: false,

  init() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const selectors = [
      '.image-banner__image',
      '.business-model__image',
      '.brand-theme__image',
      '.company-section__team-image',
      '.distillery__image'
    ];
    this.items = Array.from(document.querySelectorAll(selectors.join(',')));
    if (!this.items.length) return;

    window.addEventListener('scroll', () => this.requestTick(), { passive: true });
    window.addEventListener('resize', () => this.requestTick(), { passive: true });
    this.update();
  },

  requestTick() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => this.update());
  },

  update() {
    const vh = window.innerHeight;
    this.items.forEach((el) => {
      // Measure the (untransformed) wrapper rather than the image itself,
      // since the image already carries the transform we're about to set.
      const rect = (el.parentElement || el).getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > vh + 200) return;
      const centerOffset = rect.top + rect.height / 2 - vh / 2;
      const shift = centerOffset * -0.08;
      el.style.transform = `translateY(${shift}px)`;
    });
    this.ticking = false;
  }
};

// =============================================================================
// Cinematic Hero — opt-in reveal for full-height image-banner heroes
// (sections/image-banner.liquid, cinematic_reveal setting; Water Dragon /
// Drift Mark distillery pages). The "hidden" loading state is CSS's own
// default for .image-banner--cinematic (see assets/theme.css) — no class
// this module adds runs a race against first paint. This module's only job
// is deciding *when* to add image-banner--revealed, which transitions
// everything to its final state.
// =============================================================================

const CinematicHero = {
  init() {
    const sections = document.querySelectorAll('[data-cinematic-reveal="true"]');
    if (!sections.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    sections.forEach((section) => this.setup(section));
  },

  setup(section) {
    const reveal = () => {
      // AgeVerification (and any other blocking modal) toggles this class
      // on <body>. If it's up, revealing now would finish the whole
      // animation behind it, so a fresh visitor (no cookie/localStorage
      // yet, e.g. private browsing) never actually sees the motion —
      // wait for it to clear first.
      if (document.body.classList.contains('modal-open')) {
        const observer = new MutationObserver(() => {
          if (!document.body.classList.contains('modal-open')) {
            observer.disconnect();
            section.classList.add('image-banner--revealed');
          }
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return;
      }
      section.classList.add('image-banner--revealed');
    };

    const img = section.querySelector('.image-banner__image');
    if (!img || img.complete) {
      // Already cached — still defer a frame so the transition has a
      // hidden state to animate from instead of jumping straight in.
      requestAnimationFrame(reveal);
      return;
    }

    img.addEventListener('load', reveal, { once: true });
    img.addEventListener('error', reveal, { once: true });
    // Safety net in case neither event fires (shouldn't normally happen).
    setTimeout(reveal, 6000);
  }
};

// =============================================================================
// TOP Page Side Navigation — scroll-spy for the in-page dot nav
// =============================================================================

const TopSideNav = {
  init() {
    const nav = document.querySelector('[data-top-side-nav]');
    if (!nav) return;

    const links = Array.from(nav.querySelectorAll('[data-nav-target]'));
    const linkTargets = links
      .map((link) => ({ link, section: resolveSectionAnchor(`#${link.dataset.navTarget}`) }))
      .filter((entry) => entry.section);

    if (!linkTargets.length) return;

    const setActive = (target) => {
      linkTargets.forEach(({ link, section }) => {
        link.closest('.top-side-nav__item').classList.toggle(
          'top-side-nav__item--active',
          section === target
        );
      });
    };

    // Scroll-position based (rather than IntersectionObserver rootMargin
    // math, which misbehaves for the first/last sections when they're
    // taller than the intersection band). The "current" section is the
    // last one whose top has scrolled past a reference line near the
    // upper part of the viewport — same approach most scroll-spy UIs use.
    let ticking = false;

    const updateActive = () => {
      ticking = false;
      const referenceLine = window.innerHeight * 0.4;
      let current = linkTargets[0].section;
      for (const { section } of linkTargets) {
        if (section.getBoundingClientRect().top <= referenceLine) {
          current = section;
        } else {
          break;
        }
      }
      setActive(current);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateActive);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    updateActive();
  }
};

// =============================================================================
// Countdown — target日時までの残り日/時/分/秒を表示する
// =============================================================================

const Countdown = {
  init() {
    document.querySelectorAll('[data-countdown]').forEach((el) => {
      const target = new Date(el.dataset.countdown).getTime();
      if (isNaN(target)) return;

      const render = () => {
        const diff = target - Date.now();
        if (diff <= 0) {
          el.querySelectorAll('[data-countdown-days], [data-countdown-hours], [data-countdown-minutes], [data-countdown-seconds]').forEach((n) => {
            n.textContent = '0';
          });
          clearInterval(timer);
          return;
        }
        const days = Math.floor(diff / 86400000);
        const hours = Math.floor((diff % 86400000) / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);

        const setText = (selector, value) => {
          const node = el.querySelector(selector);
          if (node) node.textContent = String(value).padStart(2, '0');
        };
        setText('[data-countdown-days]', days);
        setText('[data-countdown-hours]', hours);
        setText('[data-countdown-minutes]', minutes);
        setText('[data-countdown-seconds]', seconds);
      };

      render();
      const timer = setInterval(render, 1000);
    });
  }
};

// =============================================================================
// Generic Modal — data-modal-trigger="id" opens #id, data-modal-close inside
// it closes it (overlay click, close button, Escape key). Currently used by
// the product page's shipping-rate-modal snippet; reusable for future modals
// without writing new open/close JS each time.
// =============================================================================

const GenericModal = {
  init() {
    document.querySelectorAll('[data-modal-trigger]').forEach((trigger) => {
      const targetId = trigger.getAttribute('data-modal-trigger');
      const modal = document.getElementById(targetId);
      if (!modal) return;

      trigger.addEventListener('click', () => {
        modal.classList.remove('is-hidden');
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('has-open-modal');
      });

      const close = () => {
        modal.classList.add('is-hidden');
        modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('has-open-modal');
      };

      modal.querySelectorAll('[data-modal-close]').forEach((closeEl) => {
        closeEl.addEventListener('click', close);
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !modal.classList.contains('is-hidden')) close();
      });
    });
  }
};

// 10%OFF割引コード未登録リマインド（トークン保有者向け）。
// 表示要否（needsDiscountReminder）はtheme.liquid側で、顧客自身の
// discount_valid_untilが今月のコードをカバー済みかどうかで判定済み。
// 登録が完了すればこのフラグ自体がfalseになるため、次にページを開いた時点で
// 自動的に出なくなる。ここでは「うるさくなりすぎない」ための表示場所と
// スヌーズ（一定時間の再表示抑制）だけを担当する。
// 表示場所はサイト全体の割り込みモーダルではなく、カートドロワー／カートページの
// 上部という「割引を気にするタイミング」に限定する。
const DiscountReminder = {
  STORAGE_KEY: 'whiskey_discount_reminder_snoozed_until',
  SNOOZE_HOURS: 24,

  shouldShow() {
    const membership = WhiskeyTheme.membership || {};
    if (!membership.needsDiscountReminder) return false;
    try {
      const snoozedUntil = localStorage.getItem(this.STORAGE_KEY);
      if (snoozedUntil && Date.now() < Number(snoozedUntil)) return false;
    } catch (e) {
      // localStorage not available — 通常通り表示してよい
    }
    return true;
  },

  snooze() {
    try {
      localStorage.setItem(this.STORAGE_KEY, String(Date.now() + this.SNOOZE_HOURS * 60 * 60 * 1000));
    } catch (e) {}
  },

  build() {
    const membership = WhiskeyTheme.membership || {};
    const el = document.createElement('div');
    el.className = 'cart-discount-reminder';
    el.innerHTML = `
      <span class="cart-discount-reminder__text">今月の10%OFFコードが未登録です</span>
      <a href="${membership.membershipPageUrl || '#'}" class="cart-discount-reminder__link">登録する</a>
      <button type="button" class="cart-discount-reminder__close" aria-label="閉じる">×</button>
    `;
    // 「登録する」は登録画面を見に行くだけなので、まだ登録が完了したとは限らない。
    // スヌーズは「×」で明示的に閉じた場合のみ（登録が終われば表示条件自体がfalseになる）。
    el.querySelector('.cart-discount-reminder__close').addEventListener('click', () => {
      this.snooze();
      el.remove();
    });
    return el;
  },

  // カートドロワー: フッター（小計の直前）に差し込む。footer自体はCart.render()の
  // たびに丸ごと再生成されないため、毎回既存のバナーを消してから要否を再判定する。
  renderIntoDrawer(footerEl) {
    if (!footerEl) return;
    const existing = footerEl.querySelector('.cart-discount-reminder');
    if (existing) existing.remove();
    if (!this.shouldShow()) return;
    const subtotalEl = footerEl.querySelector('.cart-drawer__subtotal');
    if (subtotalEl) {
      footerEl.insertBefore(this.build(), subtotalEl);
    } else {
      footerEl.prepend(this.build());
    }
  },

  // カートページ: サーバーサイドで用意されたプレースホルダーに差し込む（初回ロード時のみ）
  init() {
    const placeholder = document.getElementById('cart-discount-reminder-placeholder');
    if (!placeholder || !this.shouldShow()) return;
    placeholder.replaceWith(this.build());
  }
};

const ExternalLinks = {
  internalHosts: ['whiskey-co-3.myshopify.com', 'whiskey.co.jp', window.location.hostname],

  processLink(a) {
    if (a.dataset.extLinkChecked) return;
    a.dataset.extLinkChecked = 'true';

    const href = a.getAttribute('href');
    if (!href || href.charAt(0) === '#' || /^(mailto:|tel:|javascript:)/i.test(href)) return;

    let url;
    try {
      url = new URL(href, window.location.href);
    } catch (e) {
      return;
    }

    if (!this.internalHosts.includes(url.hostname)) {
      a.setAttribute('target', '_blank');
      const rel = new Set((a.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
      rel.add('noopener');
      rel.add('noreferrer');
      a.setAttribute('rel', [...rel].join(' '));
    }
  },

  processAll(root) {
    (root || document).querySelectorAll('a[href]').forEach((a) => this.processLink(a));
  },

  init() {
    this.processAll(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('a[href]')) this.processLink(node);
          if (node.querySelectorAll) this.processAll(node);
        });
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
};

// =============================================================================
// Initialize Everything
// =============================================================================

function initTheme() {
  // Age verification (first thing, blocks content if needed)
  AgeVerification.init();

  // Summer holiday notice popup (product pages / contact page) — waits for
  // the age gate above to clear before showing.
  SummerHolidayNotice.init();

  // Header sticky behavior
  Header.init();

  // Mobile menu
  MobileMenu.init();

  // Cart (load count + bind events)
  Cart.init();
  CartCount.init();

  // Add to cart
  AddToCart.init();

  // Smooth scroll
  SmoothScroll.init();

  // Scroll animations
  AnimationObserver.init();

  // FiNANCiE UI
  FinancieUI.init();

  // Accordion
  Accordion.init();

  // Tabs
  Tabs.init();

  // Serving recommendation carousel (product detail)
  ServingRecommendationCarousel.init();

  // Countdown
  Countdown.init();

  // TOP page side navigation (scroll-spy)
  TopSideNav.init();

  // Light parallax on photos
  Parallax.init();

  // Cinematic hero reveal (distillery page full-height heroes)
  CinematicHero.init();

  // Alternating section background gradients
  SectionGradient.init();

  // Hero KV background video — keep it covering the container with no gaps
  HeroVideoCover.init();

  // Featured products — horizontal scroll carousel (manual, no autoplay)
  ProductCarousel.init();

  // Generic modal open/close (product page shipping-rate modal, etc.)
  GenericModal.init();

  // 10%OFF discount code reminder — cart page placeholder (drawer version hooks into Cart.render)
  DiscountReminder.init();

  // Open links to domains other than the shop's own to a new tab
  ExternalLinks.init();
}

// Run when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTheme);
} else {
  initTheme();
}

// =============================================================================
// Public API (expose for section scripts and third-party integrations)
// =============================================================================

window.WhiskeyTheme = Object.assign(WhiskeyTheme, {
  formatMoney,
  showToast,
  checkFinancieToken,
  Cart,
  AddToCart,
  AnimationObserver,
  AgeVerification,
  SummerHolidayNotice,
  TopSideNav,
  Parallax,
  SectionGradient
});
